import React, { useState, useEffect } from "react";
import { Bell_icon } from "../../assets/images";
import {
  DownOutlined,
  EnvironmentFilled,
  EnvironmentOutlined,
  LeftOutlined,
  UserOutlined,
} from "@ant-design/icons";
import "./BaseLayout.less";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { Avatar, Badge, Button, Modal, List, Typography, message } from "antd";
import {
  ROUTE_ROOT,
  ROUTE_CLASSROOM,
  ROUTE_EDUCENTRE,
  ROUTE_NOTES,
  ROUTE_PROFILE,
  ROUTE_ASSIGNMENTS,
  ROUTE_NOTIFICATIONS,
  ROUTE_PAYMENTS,
} from "../../constant/routes";
import { useGetUserByIDQuery } from "../../redux/services/user";
import { useGetNotificationsQuery } from "../../redux/services/notification";
import { useSelector, useDispatch } from "react-redux";
import { setUserLocation } from "../../redux/features/authSlice";

const { Title, Text } = Typography;

const BaseLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { student_id, userLocation } = useSelector((state) => state.auth);
  const { data: user, isLoading: userLoading } =
    useGetUserByIDQuery(student_id);
  const {
    data: notifications,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery({ student_id: student_id });

  const [activeRoute, setActiveRoute] = useState("");
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [lastMainRoute, setLastMainRoute] = useState(ROUTE_ROOT);
  const [savedAddresses, setSavedAddresses] = useState([
    { id: 1, name: "Home", address: "123 Main St, City" },
    { id: 2, name: "School", address: "456 Education Blvd, Town" },
  ]);
  // Initialize currentLocation from Redux store if available
  const [currentLocation, setCurrentLocation] = useState(userLocation || null);

  // Calculate unread notifications count
  const unreadCount = React.useMemo(() => {
    if (!notifications?.data) return 0;

    // Filter only unread notifications and count them
    return notifications.data.filter((notification) => !notification.is_read)
      .length;
  }, [notifications]);

  useEffect(() => {
    setActiveRoute(location.pathname);
  }, [location]);

  // Add polling for notifications
  useEffect(() => {
    // Initial fetch
    refetchNotifications();

    // Set up polling interval
    const notificationInterval = setInterval(() => {
      refetchNotifications();
    }, 60000); // Check every 60 seconds

    // Clean up interval on component unmount
    return () => clearInterval(notificationInterval);
  }, [student_id, refetchNotifications]);

  const handleNavigation = (route) => {
    setActiveRoute(route);
    // If the route is one of the main routes, update lastMainRoute
    if (
      [ROUTE_ROOT, ROUTE_CLASSROOM, ROUTE_EDUCENTRE, ROUTE_NOTES].includes(
        route
      )
    ) {
      setLastMainRoute(route);
    }
    navigate(route);
  };

  const navigateBack = () => {
    navigate(-1); // Navigate to the previous page
  };

  const navigateToProfile = () => {
    navigate(ROUTE_PROFILE);
  };

  const showLocationModal = () => {
    setIsLocationModalVisible(true);
  };

  const handleLocationCancel = () => {
    setIsLocationModalVisible(false);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          // Set local state
          setCurrentLocation({
            lat: latitude,
            lng: longitude,
            address: "Current Location",
          });

          // Save location to Redux store
          dispatch(
            setUserLocation({
              latitude,
              longitude,
              address: "Current Location",
            })
          );

          message.success("Location updated successfully");
          setIsLocationModalVisible(false);
        },
        (error) => {
          console.error("Error getting location:", error);
          message.error(
            "Could not get your location. Please check your permissions."
          );
        }
      );
    } else {
      message.error("Geolocation is not supported by this browser.");
    }
  };

  const selectSavedAddress = (address) => {
    // Set local state
    setCurrentLocation({
      address: address.address,
      name: address.name,
    });

    // Save to Redux store
    dispatch(
      setUserLocation({
        address: address.address,
        name: address.name,
      })
    );

    message.success(`Location set to ${address.name}`);
    setIsLocationModalVisible(false);
  };

  const getHeaderTitle = () => {
    switch (activeRoute) {
      case ROUTE_ROOT:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              icon={<UserOutlined />}
              className='avatar'
              onClick={navigateToProfile}
              style={{ cursor: "pointer" }}
            />
            <span className='basename'>Hi {user?.user?.name || "Buddy"}</span>
          </div>
        );
      case ROUTE_CLASSROOM:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>Classroom</span>
          </div>
        );
      case ROUTE_EDUCENTRE:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>EduCentre</span>
          </div>
        );
      case ROUTE_NOTES:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>Notes</span>
          </div>
        );
      case ROUTE_PROFILE:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>Profile</span>
          </div>
        );
      case ROUTE_ASSIGNMENTS:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>All Assignments</span>
          </div>
        );
      case ROUTE_NOTIFICATIONS:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>Notifications</span>
          </div>
        );
      case ROUTE_PAYMENTS:
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
            <span className='basename'>Payments</span>
          </div>
        );

      default:
        return (
          <>
            <LeftOutlined className='back-arrow' onClick={navigateBack} />
          </>
        );
    }
  };

  // Check if current path matches any main route
  const isInHome = useMatch({ path: ROUTE_ROOT, end: true });
  const isInClassroom = useMatch({ path: `${ROUTE_CLASSROOM}/*` });
  const isInEducentre = useMatch({ path: `${ROUTE_EDUCENTRE}/*` });
  const isInNotes = useMatch({ path: `${ROUTE_NOTES}/*` });

  // Determine which nav item should be active
  const getActiveNav = () => {
    if (isInHome) return ROUTE_ROOT;
    if (isInClassroom) return ROUTE_CLASSROOM;
    if (isInEducentre) return ROUTE_EDUCENTRE;
    if (isInNotes) return ROUTE_NOTES;
    return lastMainRoute; // Fall back to last main route if not on a main route
  };

  const currentActiveNav = getActiveNav();

  return (
    <div className='base-layout'>
      <header className='base-header'>
        <div className='location-container'>
          <Button
            type='text'
            icon={<EnvironmentFilled className='location-icon' />}
            onClick={showLocationModal}
            style={{ color: "white" }}
          >
            {currentLocation ? (
              <Text
                ellipsis
                style={{ maxWidth: 120, color: "white", fontSize: "12px" }}
              >
                {currentLocation.name || currentLocation.address}{" "}
                <DownOutlined />
              </Text>
            ) : (
              "Set Location"
            )}
          </Button>
        </div>
        <div className='header-content'>
          {getHeaderTitle()}

          {/* Only show badge if there are unread notifications */}
          <Badge
            count={
              unreadCount > 0 ? (unreadCount > 99 ? "99+" : unreadCount) : 0
            }
            size='small'
            showZero={false}
          >
            <Button
              type='text'
              icon={<img src={Bell_icon} alt='Notification icon' />}
              onClick={() => handleNavigation(ROUTE_NOTIFICATIONS)}
            ></Button>
          </Badge>
        </div>
      </header>

      {/* Location Selection Modal */}
      <Modal
        title='Select Location'
        open={isLocationModalVisible}
        onCancel={handleLocationCancel}
        footer={null}
        centered
      >
        {currentLocation && (
          <div style={{ marginBottom: 20 }}>
            <Title level={5}>Current Location</Title>
            <div
              style={{
                padding: "12px 16px",
                background: "#f0f2f5",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <EnvironmentFilled
                style={{ color: "#1677ff", marginRight: 8, fontSize: "24px" }}
              />
              <div>
                <div style={{ fontWeight: "bold" }}>
                  {currentLocation.name || "Current Location"}
                </div>
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {currentLocation.lat && currentLocation.lng
                    ? `Lat: ${currentLocation.lat.toFixed(
                        4
                      )}, Lng: ${currentLocation.lng.toFixed(4)}`
                    : "Address not available"}
                </div>
              </div>
            </div>
          </div>
        )}

        <Button
          type='primary'
          block
          onClick={getCurrentLocation}
          style={{ marginBottom: 16 }}
        >
          Get Current Location
        </Button>

        <Title level={5} style={{ marginTop: 16 }}>
          Saved Addresses
        </Title>
        <List
          itemLayout='horizontal'
          dataSource={savedAddresses}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button onClick={() => selectSavedAddress(item)}>
                  Select
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<EnvironmentOutlined />}
                title={item.name}
                description={item.address}
              />
            </List.Item>
          )}
        />
      </Modal>

      <main className='base-content'>{children}</main>

      <footer className='base-footer'>
        <nav className='footer-nav'>
          <button
            className={`nav-item ${
              currentActiveNav === ROUTE_ROOT ? "active" : ""
            }`}
            onClick={() => handleNavigation(ROUTE_ROOT)}
          >
            <svg
              width='21'
              height='20'
              viewBox='0 0 21 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                id='Vector'
                d='M10.9333 0.160044C10.8126 0.0567576 10.6589 0 10.5 0C10.3411 0 10.1874 0.0567576 10.0667 0.160044L0.5 8.36004V18C0.5 18.5305 0.710714 19.0392 1.08579 19.4143C1.46086 19.7893 1.96957 20 2.5 20H7.83333C8.01014 20 8.17971 19.9298 8.30474 19.8048C8.42976 19.6798 8.5 19.5102 8.5 19.3334V15.3334C8.5 14.8029 8.71071 14.2942 9.08579 13.9192C9.46086 13.5441 9.96957 13.3334 10.5 13.3334C11.0304 13.3334 11.5391 13.5441 11.9142 13.9192C12.2893 14.2942 12.5 14.8029 12.5 15.3334V19.3334C12.5 19.5102 12.5702 19.6798 12.6953 19.8048C12.8203 19.9298 12.9899 20 13.1667 20H18.5C19.0304 20 19.5391 19.7893 19.9142 19.4143C20.2893 19.0392 20.5 18.5305 20.5 18V8.36004L10.9333 0.160044Z'
                fill='currentColor'
              />
            </svg>
            <span className='nav-text'>Home</span>
          </button>
          <button
            className={`nav-item ${
              currentActiveNav === ROUTE_CLASSROOM ? "active" : ""
            }`}
            onClick={() => handleNavigation(ROUTE_CLASSROOM)}
          >
            <svg
              width='21'
              height='20'
              viewBox='0 0 21 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g id='Group'>
                <path
                  id='Vector'
                  d='M10.5 9.16669C12.5711 9.16669 14.25 7.48775 14.25 5.41669C14.25 3.34562 12.5711 1.66669 10.5 1.66669C8.42893 1.66669 6.75 3.34562 6.75 5.41669C6.75 7.48775 8.42893 9.16669 10.5 9.16669Z'
                  stroke='currentColor'
                  stroke-width='1.66667'
                  stroke-linejoin='round'
                />
                <path
                  id='Vector_2'
                  d='M2.58325 18.3333C2.58325 14.8179 5.15617 11.5366 7.33325 10.8333C7.33325 10.8333 9.31242 12.9429 10.4999 14.3491L13.6666 10.8333C15.4478 11.0679 18.4166 14.8179 18.4166 18.3333'
                  stroke='currentColor'
                  stroke-width='1.66667'
                  stroke-linejoin='round'
                />
                <path
                  id='Vector_3'
                  d='M1.33325 18.3333H19.6666'
                  stroke='currentColor'
                  stroke-width='1.66667'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                />
              </g>
            </svg>

            <span className='nav-text'>Classroom</span>
          </button>
          <button
            className={`nav-item ${
              currentActiveNav === ROUTE_EDUCENTRE ? "active" : ""
            }`}
            onClick={() => handleNavigation(ROUTE_EDUCENTRE)}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g id='bxs:school'>
                <path
                  id='Vector'
                  d='M17.5001 8.33335H15.8334V3.33335H16.6667V1.66669H3.33341V3.33335H4.16675V8.33335H2.50008C2.27907 8.33335 2.06711 8.42115 1.91083 8.57743C1.75455 8.73371 1.66675 8.94567 1.66675 9.16669V16.6667H18.3334V9.16669C18.3334 8.94567 18.2456 8.73371 18.0893 8.57743C17.9331 8.42115 17.7211 8.33335 17.5001 8.33335ZM11.6667 15V11.6667H8.33341V15H5.83341V3.33335H14.1667V15H11.6667Z'
                  fill='currentColor'
                />
                <path
                  id='Vector_2'
                  d='M7.5 5H9.16667V6.66667H7.5V5ZM10.8333 5H12.5V6.66667H10.8333V5ZM7.5 8.33333H9.16667V10H7.5V8.33333ZM10.8333 8.33333H12.5V10H10.8333V8.33333Z'
                  fill='currentColor'
                />
              </g>
            </svg>

            <span className='nav-text'>Educenter</span>
          </button>
          <button
            className={`nav-item ${
              currentActiveNav === ROUTE_NOTES ? "active" : ""
            }`}
            onClick={() => handleNavigation(ROUTE_NOTES)}
          >
            <svg
              width='21'
              height='20'
              viewBox='0 0 21 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g id='Default notes'>
                <path
                  id='Vector'
                  d='M8.00008 5.83333H13.0001M8.00008 9.16667H13.0001M8.00008 12.5H11.3334M4.66675 4.16667C4.66675 3.72464 4.84234 3.30072 5.1549 2.98816C5.46746 2.67559 5.89139 2.5 6.33341 2.5H14.6667C15.1088 2.5 15.5327 2.67559 15.8453 2.98816C16.1578 3.30072 16.3334 3.72464 16.3334 4.16667V15.8333C16.3334 16.2754 16.1578 16.6993 15.8453 17.0118C15.5327 17.3244 15.1088 17.5 14.6667 17.5H6.33341C5.89139 17.5 5.46746 17.3244 5.1549 17.0118C4.84234 16.6993 4.66675 16.2754 4.66675 15.8333V4.16667Z'
                  stroke='currentColor'
                  stroke-width='1.66667'
                  stroke-linecap='round'
                  stroke-linejoin='round'
                />
              </g>
            </svg>

            <span className='nav-text'>Repository</span>
          </button>
        </nav>
      </footer>
    </div>
  );
};

export default BaseLayout;
